<template>
    <m-pageframe :title="title">
        <v-container fluid>
            <v-row dense>
                <v-col>
                    <v-card>
                        <v-card-title>Message Patterns</v-card-title>
                        <v-card-text>
                            <ul class="text-body-1">
                                <li
                                    v-for="(pattern, index) in mixin_bulk_message_status.message_patterns"
                                    :key="index"
                                >
                                    {{ pattern }}
                                </li>
                            </ul>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col>
                    <v-card>
                        <v-card-title>Message Status</v-card-title>
                        <v-card-text class="font-weight-medium text-h6">
                            {{ mixin_status_description }}

                            <div
                                v-if="mixin_bulk_message_status.cancellation_reason"
                                class="font-weight-medium text-subtitle-1"
                            >
                                {{ mixin_bulk_message_status.cancellation_reason }}
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col>
                    <v-card>
                        <v-card-title>Pending</v-card-title>
                        <v-card-text class="font-weight-medium text-h6">
                            {{ this.$rc.pretty(mixin_bulk_message_status.count_pending, 'int') }}
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col>
                    <v-card>
                        <v-card-title>Sent</v-card-title>
                        <v-card-text class="font-weight-medium text-h6">
                            {{ this.$rc.pretty(mixin_bulk_message_status.count_sent, 'int') }}
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col>
                    <v-card>
                        <v-card-title>Not Sent</v-card-title>
                        <v-card-text class="font-weight-medium text-h6">
                            {{ this.$rc.pretty(mixin_not_sent_total, 'int') }}
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col>
                    <v-card>
                        <v-card-title>Not Sent Detail</v-card-title>
                        <v-card-subtitle v-if="mixin_filtered_total > 0">
                            Want to resend this bulk message to recipients who were filtered out? <a
                                href="https://redchirp.com/how-to-send-a-bulk-message-to-previously-filtered-recipients/"
                                target="_blank"
                            >Learn how here</a>.
                        </v-card-subtitle>

                        <v-simple-table>
                            <template v-slot:default>
                                <thead>
                                    <tr>
                                        <th class="text-left">
                                            Recipient Status
                                        </th>
                                        <th class="text-left">
                                            Recipient Count
                                        </th>
                                        <th class="text-center">
                                            Description
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <recipient-table-row
                                        label='Cancelled'
                                        :value="mixin_bulk_message_status.count_cancelled"
                                        description="Sending was cancelled before these recipients were sent"
                                    ></recipient-table-row>

                                    <recipient-table-row
                                        label='Failed'
                                        v-if="mixin_failed_total > 0"
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Unable to Generate Message'
                                        :value="mixin_bulk_message_status.count_failed_unable_to_generate_message"
                                        description="Unable to generate the message text to send from the Message Patterns provided"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Other'
                                        :value="mixin_bulk_message_status.count_failed_other"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Failed Total'
                                        :value="mixin_failed_total"
                                        indented
                                        additional_classes="font-weight-medium"
                                    ></recipient-table-row>

                                    <recipient-table-row
                                        label='Filtered'
                                        v-if="mixin_filtered_total > 0"
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Consent'
                                        :value="mixin_bulk_message_status.count_filtered_consent"
                                        description="No consent on file in RedChirp for these types of bulk messages"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Days Since Last Bulk Message'
                                        :value="mixin_bulk_message_status.count_filtered_days_since_last_bulk_message"
                                        description="Recipient received another bulk message within the configured number of days (configured in Bulk Message Settings)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Duplicate'
                                        :value="mixin_bulk_message_status.count_filtered_duplicate"
                                        description="A message was already sent to the recipient as part of this bulk message"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Order History'
                                        :value="mixin_bulk_message_status.count_filtered_order_history"
                                        description="Recipient has not placed an order within the configured number of months (configured in Bulk Message Settings)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Previously Opted Out'
                                        :value="mixin_bulk_message_status.count_filtered_previously_opted_out"
                                        description="Recipient previously opted out of these types of bulk messages"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Received Other Bulk Message(s)'
                                        :value="mixin_bulk_message_status.count_filtered_received_bulk_message"
                                        description="Recipient already received a related bulk message (configured in Bulk Message Settings)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Send Time Restriction'
                                        :value="mixin_bulk_message_status.count_filtered_send_time_restrictions"
                                        description="Send attempted during a restricted time, e.g. holiday (configured in Bulk Message Settings)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Simple Do Not Send After'
                                        :value="mixin_bulk_message_status.count_filtered_simple_do_not_send_after_wall_time"
                                        description="Send attempted after the 'avoid sending after' time specified in this bulk message (configured in Bulk Message Settings)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='TCPALitigatorList.com'
                                        :value="mixin_bulk_message_status.count_filtered_tcpa_litigators_list"
                                        description="Recipient phone number appears in a database of known litigants and do not call list complainers"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Texas Do Not Call List'
                                        :value="mixin_bulk_message_status.count_filtered_texas_do_not_call_list"
                                        description="Recipient phone number appears in the Texas Do Not Call list (www.texasnocall.com)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='In Excluded Location'
                                        :value="mixin_bulk_message_status.count_filtered_recipient_in_locations"
                                        description="Recipient address (from an integrated CRM) or phone number appears in an excluded location (e.g. Texas)"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Twilio Carrier Deactivation Reports'
                                        :value="mixin_bulk_message_status.count_filtered_twilio_carrier_deactivation_reports"
                                        description="Phone number may have been reassigned"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Other'
                                        :value="mixin_bulk_message_status.count_filtered_other"
                                        indented
                                    ></recipient-table-row>
                                    <recipient-table-row
                                        label='Filtered Total'
                                        :value="mixin_filtered_total"
                                        indented
                                        additional_classes="font-weight-medium"
                                    ></recipient-table-row>

                                    <recipient-table-row
                                        label='Total'
                                        :value="mixin_not_sent_total"
                                        additional_classes="font-weight-bold row-total"
                                    ></recipient-table-row>
                                </tbody>
                            </template>
                        </v-simple-table>
                    </v-card>
                </v-col>
            </v-row>

            <v-row dense>
                <v-col>
                    <v-card>
                        <v-card-title>Links</v-card-title>
                        <v-card-actions>
                            <m-list-buttons :items="links_buttons" />
                        </v-card-actions>
                    </v-card>
                </v-col>
            </v-row>
        </v-container>
    </m-pageframe>
</template>

<script>
import RecipientTableRow from './view-bulk-message-send-summary-recipient-table-row.vue';
import BulkMessageSendStatusMixin from '@/mixins/bulk-message-send-status.js';

export default {
    mixins: [BulkMessageSendStatusMixin],

    data() {
        return {
            // TODO Conditional links?
            links_buttons: [
                { 'title': 'Share', 'sub_title': 'Share this link with fellow RedChirp users in your account', 'icon': 'mdi-share-outline', 'action': this.onShare },
                { 'title': 'Recipients Detail (Excel)', 'sub_title': 'Export to Excel with all recipients and their current status', 'icon': 'mdi-file-excel-outline', 'action': this.onExportRecipientDetail },
                { 'title': 'Order Attribution', 'sub_title': 'View analytics for orders attributed to this bulk message', 'icon': 'mdi-google-analytics', 'action': this.linkToOrderAttribution },
                { 'title': 'Bulk Message Settings', 'sub_title': 'View settings for this bulk message', 'icon': 'mdi-comment-text-multiple', 'action': this.linkToBulkMessageSettings },
                { 'title': 'Change Log', 'sub_title': 'View account change log entries for this bulk message', 'icon': 'mdi-format-list-bulleted-square', 'action': this.linkToChangeLog },
            ],
        }
    },

    mounted() {
        this.update();
    },

    components: {
        RecipientTableRow
    },

    methods: {
        update() {
            this.mixinGetSendStatusDetail(this.$route.params.bulk_message_id, () => {
                if (!this.mixin_send_complete) {
                    window.setTimeout(this.update, 5000);
                }
            });
        },

        onShare() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'LIST: /api/v1/organizations/employees/labels',

                'params': {
                    'organization_id': this.mixin_bulk_message_status.organization_id,
                },

                'on_success': (data) => {
                    this.pickShareRecipients(data.results);
                },

                'on_error': 'Unable to get employee list'
            });
        },

        pickShareRecipients(employees) {
            let choices = employees
                .filter(e => !e.is_archived)
                .map(e => {
                    return {
                        'text': e.employee_label,
                        'value': e.employee_id,
                    };
                }
                );

            this.$rc.pick({
                'title': 'Users to Share With',
                'content_before': null,
                'content_after': 'Selected users will get a text and/or email with a link to this page',

                'choices': choices,

                'multiple': true,
                'type': 'select',
                'select_label': 'Select Users',
                'initial_value': null,
                'unset_label': null,

                'on_ok': (value) => {
                    this.doShareWithRecipients(value);
                },

                'on_cancel': () => { }
            });
        },

        doShareWithRecipients(employee_ids) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/messages/send-status-detail/share',

                'params': {
                    'organization_id': this.mixin_bulk_message_status.organization_id,
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                    'employee_ids': employee_ids,
                },

                'on_success': (data) => {
                    this.$rc.snackbar('Sharing ' + (1 == employee_ids.length ? 'message' : 'messages') + ' sent');
                },

                'on_error': 'Unable to share'
            });
        },

        onExportRecipientDetail() {
            this.$rc.excelExport(
                'EXPORT: /api/v1/bulk/recipients/export/:organization_id/:bulk_message_id',
                {
                    'organization_id': this.mixin_bulk_message_status.organization_id,
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                }
            );
        },

        linkToOrderAttribution() {
            let route = {
                'name': 'analytics-one-bulk-message-analytics-group',
                'params': {
                    'organization_id': this.mixin_bulk_message_status.organization_id,
                    'analytics_group_id': this.mixin_bulk_message_status.comp_analytics_group_id,
                }
            };

            let resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        },

        linkToBulkMessageSettings() {
            let route = {
                'name': 'view-bulk-message',
                'params': {
                    'organization_id': this.mixin_bulk_message_status.organization_id,
                    'bulk_message_id': this.mixin_bulk_message_status.bulk_message_id,
                }
            };

            let resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        },

        linkToChangeLog() {
            let route = {
                'name': 'account-change-log',
                'params': {
                    'organization_id': this.mixin_bulk_message_status.organization_id
                },
                'query': {
                    'subject_uuid': this.mixin_bulk_message_status.bulk_message_id
                }
            };

            let resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        }
    },

    computed: {
        title() {
            if (this.mixin_bulk_message_status?.label) {
                return 'Bulk Message Status: ' + this.mixin_bulk_message_status.label;
            }

            return 'Bulk Message Status';
        },
    }
}


</script>

<style lang="scss" scoped></style>